/**
 * useSyncQueue — auto-sync composable for pending complaint submissions (P.4).
 *
 * Architecture:
 * - Module-level singleton shared refs (pendingCount, isSyncing, lastSyncSummary, etc.)
 *   so every component that calls `useSyncQueue()` sees the SAME reactive state.
 * - Module-level `online`/`offline` window listeners installed ONCE per page
 *   session — guaranteed by the `_syncListenerInstalled` flag.
 * - First call to `useSyncQueue()` (typically from `<SyncQueueBootstrap />` mounted
 *   in `App.vue`) is what installs the listeners. Subsequent calls (e.g. from a
 *   banner mounted in `AppLayout.vue`, or from the form page) just return the
 *   shared refs and helpers.
 *
 * Usage:
 *   // App.vue — install listeners at app start
 *   <SyncQueueBootstrap />
 *
 *   // AppLayout.vue — show global sync status banner
 *   const { pendingCount, isSyncing, lastSyncSummary } = useSyncQueue();
 *   <SyncQueueIndicator v-if="pendingCount || isSyncing || lastSyncSummary" />
 *
 *   // ComplaintNew.vue — enqueue a failed submission
 *   const { enqueueAndWatch } = useSyncQueue();
 *   await enqueueAndWatch(payload);  // banner updates automatically
 *
 * Acceptance:
 *   [x] Online event triggers automatic processQueue() — installed at app start
 *   [x] pendingCount reactive — single source shared across components
 *   [x] isSyncing reactive — true while processQueue() is running
 *   [x] syncResults reactive — array of ProcessResult for UI feedback
 *   [x] enqueueAndWatch(payload) — add to queue + try immediately if online
 *   [x] Items that fail after MAX_ATTEMPTS stay in queue (retry next online)
 */

import { readonly, ref, shallowReadonly, type Ref } from "vue";
import {
  enqueueSubmission,
  getPendingCount,
  processQueue,
  type ProcessResult,
  type QueuedSubmission,
} from "../utils/syncQueue";

// ─────────────────────────────────────────────────────────────────────
// Module-level shared state (singleton pattern)
// ─────────────────────────────────────────────────────────────────────

const pendingCount = ref(0);
const isSyncing = ref(false);
const lastSyncAt = ref<string | null>(null);
const syncResults = ref<ProcessResult[]>([]);
const lastSyncSummary = ref<string>("");
const lastSyncVersion = ref<number>(0); // monotonic counter for dismiss comparisons

let _syncListenerInstalled = false;
let _bootstrapCalled = false;

/** Bootstrap idempotency guard — only install listeners once. */
async function ensureBootstrap(): Promise<void> {
  if (_bootstrapCalled) return;
  _bootstrapCalled = true;

  // Refresh pending count first thing so banner can show stale queue state.
  try {
    pendingCount.value = await getPendingCount();
  } catch {
    // IndexedDB unavailable (SSR or non-browser env) → ignore.
  }

  if (_syncListenerInstalled) return;
  if (typeof window === "undefined") return;

  _syncListenerInstalled = true;
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  // If we're already online on first bootstrap, try syncing any leftover items.
  if (typeof navigator !== "undefined" && navigator.onLine) {
    void doSync();
  }
}

function onOnline(): void {
  // Small delay to let the connection stabilise before syncing.
  setTimeout(() => {
    void doSync();
  }, 1000);
}

function onOffline(): void {
  // Offline state doesn't change pendingCount — items stay queued.
  // Just log for debugging.
  if (typeof console !== "undefined") {
    console.info("[syncQueue] device went offline — queue submissions will be retried on next online event");
  }
}

async function refreshPending(): Promise<void> {
  try {
    pendingCount.value = await getPendingCount();
  } catch {
    // ignore — keep stale value
  }
}

async function doSync(): Promise<void> {
  if (isSyncing.value) return;
  isSyncing.value = true;
  syncResults.value = [];

  try {
    const results = await processQueue((result) => {
      syncResults.value = [...syncResults.value, result];
    });
    void results; // also surfaced via callback
    lastSyncAt.value = new Date().toISOString();
    lastSyncVersion.value++;

    const succeeded = results.filter((r) => r.success).length;
    const failed = results.filter((r) => !r.success).length;
    if (results.length === 0) {
      lastSyncSummary.value = "Không có khiếu nại nào cần đồng bộ.";
    } else if (failed === 0) {
      lastSyncSummary.value = `Đã đồng bộ ${succeeded} khiếu nại thành công.`;
    } else {
      lastSyncSummary.value = `Đồng bộ: ${succeeded} thành công, ${failed} thất bại (sẽ thử lại).`;
    }
  } catch (err) {
    lastSyncSummary.value = `Lỗi đồng bộ: ${err instanceof Error ? err.message : "Unknown"}`;
    lastSyncVersion.value++;
  } finally {
    isSyncing.value = false;
    await refreshPending();
  }
}

// ─────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────

export interface UseSyncQueueReturn {
  /** Number of pending/failed items still needing sync. */
  pendingCount: Readonly<Ref<number>>;
  /** True while processQueue() is running. */
  isSyncing: Readonly<Ref<boolean>>;
  /** ISO timestamp of the last completed sync (null if never). */
  lastSyncAt: Readonly<Ref<string | null>>;
  /** Results of the most recent sync run. */
  syncResults: Readonly<Ref<ProcessResult[]>>;
  /** Human-readable summary of last sync. */
  lastSyncSummary: Readonly<Ref<string>>;
  /** Monotonic version counter — increments on each sync run.
   *  Use this to compare against a `lastDismissedVersion` sentinel so
   *  the banner knows when a NEW sync result appeared after the user
   *  dismissed an earlier toast. */
  lastSyncVersion: Readonly<Ref<number>>;
  /** Add a submission to the queue and try to sync if online. */
  enqueueAndWatch: (payload: object) => Promise<QueuedSubmission>;
  /** Manually trigger a sync (refresh button or retry action). */
  triggerSync: () => Promise<void>;
  /** Clear the sync results display. */
  clearResults: () => void;
  /** Idempotently install online/offline listeners + initial pending count refresh.
   *  Called from any `useSyncQueue()` consumer; only first call does work. */
  ensureBootstrap: () => Promise<void>;
}

/**
 * Returns the singleton sync queue state + helpers.
 *
 * Idempotently installs the global `online`/`offline` event listeners on first
 * call. After that, all calls return the same reactive refs.
 */
export function useSyncQueue(): UseSyncQueueReturn {
  // Bootstrap on first call (likely from <SyncQueueBootstrap /> in App.vue).
  void ensureBootstrap();

  async function enqueueAndWatch(
    payload: object,
  ): Promise<QueuedSubmission> {
    // Persist the payload as a plain dict. Callers pass concrete shapes
    // (e.g. SubmitPayload from ComplaintNew.vue); we normalise here so
    // the IndexedDB store keeps a uniform Record<string, unknown>.
    const record = payload as Record<string, unknown>;
    const item = await enqueueSubmission(record);
    await refreshPending();

    // If online right now, trigger an immediate sync.
    if (typeof navigator !== "undefined" && navigator.onLine) {
      void doSync();
    } else {
      lastSyncSummary.value =
        `${pendingCount.value} khiếu nại đang chờ đồng bộ khi có mạng.`;
      lastSyncVersion.value++;
    }

    return item;
  }

  async function triggerSync(): Promise<void> {
    await doSync();
  }

  function clearResults(): void {
    syncResults.value = [];
    lastSyncSummary.value = "";
    lastSyncVersion.value++;
  }

  return {
    pendingCount: readonly(pendingCount),
    isSyncing: readonly(isSyncing),
    lastSyncAt: readonly(lastSyncAt),
    // shallowReadonly avoids DeepReadonly inference on array elements,
    // so ProcessResult[] stays structurally mutable from this file's
    // perspective while external readers can't reassign .value.
    syncResults: shallowReadonly(syncResults) as Readonly<Ref<ProcessResult[]>>,
    lastSyncSummary: readonly(lastSyncSummary),
    lastSyncVersion: readonly(lastSyncVersion),
    enqueueAndWatch,
    triggerSync,
    clearResults,
    ensureBootstrap,
  };
}
